package fr.frux.core.maps

/**
 * L'URL de style (donc la source de tuiles) n'est **pas** figée à la compilation de ce module :
 * chaque plateforme la fournit au démarrage (Android : `BuildConfig`, alimenté par
 * `local.properties` ou l'environnement — cf. androidApp/build.gradle.kts, ADR 0002 et #21).
 *
 * Conséquence voulue : aucune clé de fournisseur de tuiles n'est écrite dans le dépôt, et le repli
 * de développement (tuiles de démo MapLibre) reste une décision de build, pas une constante enfouie
 * dans le code carte.
 */
private var appMapStyleUrl: String? = null

/**
 * Mention d'attribution des tuiles et lien vers les crédits complets.
 *
 * Elle accompagne l'URL de style parce qu'elle en dépend : changer de fournisseur sans changer la
 * mention serait une violation de ses conditions d'utilisation.
 *
 * Ce label ne sert pas à la carte plein écran, où l'attribution affichée est celle que le SDK lit
 * dans le document de style. Il sert à la carte imbriquée, dont les ornements sont coupés, et au
 * badge de données ODbL de l'écran carte (cf. [mapAttributionCoversOpenStreetMap]).
 */
data class MapAttribution(
    val label: String,
    val url: String,
)

/** Attribution des tuiles de démo MapLibre, seul repli légitime tant qu'aucune autre n'est fournie. */
private val MAPLIBRE_DEMO_ATTRIBUTION =
    MapAttribution(
        label = "© MapLibre",
        url = "https://maplibre.org/",
    )

private var appMapAttribution: MapAttribution = MAPLIBRE_DEMO_ATTRIBUTION

fun initializeMapStyleUrl(
    styleUrl: String,
    attribution: MapAttribution = MAPLIBRE_DEMO_ATTRIBUTION,
) {
    appMapStyleUrl = styleUrl
    appMapAttribution = attribution
}

internal fun mapStyleUrl(): String =
    requireNotNull(appMapStyleUrl) {
        "URL de style carte non initialisée : appeler initializeMapStyleUrl() au démarrage de la " +
            "plateforme"
    }

internal fun mapAttribution(): MapAttribution = appMapAttribution

/**
 * Vrai si la mention d'attribution des tuiles crédite déjà les contributeurs OpenStreetMap
 * (cas du fond Protomaps). Les guidelines d'attribution OSM n'exigent qu'une seule mention pour
 * couvrir à la fois le fond de carte et les données : l'écran carte peut alors s'épargner son
 * badge de données ODbL (ADR 0010) sans rien perdre légalement.
 *
 * Couplage à tenir : en plein écran, la mention réellement affichée vient du document de style, pas
 * de ce label. Les deux doivent donc créditer OpenStreetMap ensemble. Un style qui le crédite alors
 * que ce label ne le dit pas ferait afficher le badge pour rien, l'inverse retirerait le badge sans
 * qu'aucune mention ne couvre les données.
 */
fun mapAttributionCoversOpenStreetMap(): Boolean = appMapAttribution.label.contains("OpenStreetMap", ignoreCase = true)
